"use client";

import { InferResponseType } from "hono";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/lib/hono";
import { embedCheckQuerySchema } from "@/lib/utils/lomdaV3/embedCheck";

const endpoint = client.api.storage["lomda-v3"]["embed-check"].$get;

type EmbedFramingStatus = InferResponseType<typeof endpoint, 200>["status"];

/** Whether `src` may be shown in an iframe here; anything but "blocked" keeps the iframe. */
export const useEmbedFraming = (
    src: string,
    enabled = true
): EmbedFramingStatus | "checking" => {
    const checkable =
        enabled && embedCheckQuerySchema.safeParse({ url: src }).success;
    const query = useQuery({
        queryKey: ["lomda-v3-embed-check", src],
        enabled: checkable,
        staleTime: Infinity,
        retry: false,
        queryFn: async (): Promise<EmbedFramingStatus> => {
            const res = await endpoint({ query: { url: src } });
            // Not kept: a rate limit or server error is checked again on the next mount.
            if (!res.ok) throw new Error(`Embed check failed: ${res.status}`);
            return (await res.json()).status;
        },
    });

    if (!checkable) return "unknown";
    if (query.isPending) return "checking";
    return query.data ?? "unknown";
};
